import { config } from '../config';
import {
  getIdentityInstance,
  getMonetizationInstance,
  login,
} from '../services/schibstedAccount';
import { getPreviousPageViewOrigin } from '../services/tracking/helpers/pulseOrigin';
import { isMobile } from '../utils/device';
import { getAssetPagePathV2 } from '../shared/helpers/getAssetPagePath';
import { getPulseProvider } from '../services/tracking/helpers/pulseProvider';
import { unleash } from '../services/unleash';

import type { Vendor } from '../types';
import type { PulseEvent } from '../services/tracking/pulse';
import type { Config } from '@schibsted-svp/web-player';
import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';

// Utility type to make all properties optional recursively
type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export const PLAYER_ELEMENT_ID = 'player';

const experiments: {
  id: string;
  variant: string;
}[] = [{ id: 'error-reporting', variant: 'enabled' }];

if (config.provider === 'vgtv') {
  experiments.push({ id: 'vmap', variant: 'enabled' });
}

if (config.featureToggles.enableAdsDynamicAspectRatio) {
  experiments.push({
    id: 'ads-aspect-ratio',
    variant: isMobile ? 'true' : 'false',
  });
}

experiments.push(
  ...unleash
    .getAllToggles()
    .filter(({ type }) => ['experiment', 'release'].includes(type))
    .map(({ name, variant }) => ({
      id: name,
      variant: variant.name,
    }))
);

export const SITE_OPTIONS = {
  site: config.provider === 'vgtv' ? 'vgtv' : 'abtv',
  context: 'portal',
  ads: {
    pausead: true,
    midroll: true,
  },
  experiments,
  paywallAccess: {
    enabled: true,
    getSpidIdentityInstance: getIdentityInstance,
    getSpidMonetizationInstance: getMonetizationInstance,
    usePre: config.schibstedAccount.env === 'PRE',
    message: {
      onLoginClick: login,
    },
  },
} as const;

export const SITE_CONFIG: DeepPartial<Config> = {
  vendor: config.provider,
  locale: config.provider === 'ab' ? 'se' : 'no',
  node: PLAYER_ELEMENT_ID,
  autoplay: false,
  settings: {},
  pulse: {
    provider: config.pulse.clientId,
    // @ts-expect-error invalid typedef
    decorator(
      data: PulseEvent,
      model: { getRawAsset: () => SvpAssetAdditional }
    ) {
      const decorated = { ...data };
      const asset = model.getRawAsset();

      const vendor = (asset.provider || config.provider) as Vendor;

      decorated.provider = getPulseProvider(vendor);

      decorated.object!.page = {
        '@id': `sdrn:${
          vendor === 'brandstudio' ? vendor : config.pulse.clientId
        }:video:${asset.id}`,
        '@type': config.provider === 'ab' ? 'Article' : 'Video',
        url: `${window.location.origin}${getAssetPagePathV2({
          asset,
          skipDeeplinkCheck: true,
        })}`,
      };

      decorated.origin = getPreviousPageViewOrigin();
      return decorated;
    },
  },
  plugins:
    config.provider === 'ab'
      ? {
          'https://vgc.no/player/player-plugin-sponsor-overlay-latest.js': {
            name: 'SponsorOverlayPlugin',
          },
        }
      : undefined,
  showSponsorLabel: true,
  skin: {
    name: 'vgtv2',
    url: 'https://vgc.no/player/player-skin-vgtv2-latest.css',
  },
};

if (config.provider === 'ab') {
  SITE_CONFIG.settings!.jw = {
    advertising: {
      autoplayadsmuted: true,
    },
  };
}

if (process.env.NODE_ENV === 'development') {
  SITE_CONFIG.settings!.na = config.disablePlayerAds;
}
if (config.svpApiHostname.includes('svp-stage')) {
  SITE_CONFIG.thumbnailsUrl = 'https://svp-stage.vg.no/svp/thumbnails/v1/';
  SITE_CONFIG.relatedUrl = 'https://related-stage.stream.schibsted.media/';
  SITE_CONFIG.api = 'https://svp-stage.vg.no/svp/api/v1/';
  SITE_CONFIG.tokenUrl = 'https://svp-stage.vg.no/svp/token/v1/';
}
